/**
 * AutopilotPipelinePage — kanban autopilota napędzany przez /api/creative-pipeline.
 *
 * 8 kolumn (Theriot/Haynes lifecycle): Pomysły → Do nagrania → Do montażu → Gotowe →
 * W trakcie testu → Zwycięskie / Niedziałające / Przestało działać.
 *
 * UI: <KanbanBoard /> reuse z trello-kanban-board.tsx (ten sam co ad-creator
 * creative-pipeline) — drag-and-drop HTML5, media preview, label badges.
 *
 * Per agency_client. Karty są zapełniane przez:
 *   - autopilot (briefy, winners, fatigued via AutopilotPipelineBridge)
 *   - inspiracje (link / screenshot via InspirationService)
 *   - ręczne dodanie (POST /tasks)
 */
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRef, useState } from "react";
import {
  Loader2,
  Sparkles,
  Lightbulb,
  Wand2,
  AlertTriangle,
  Check,
  X,
  Rocket,
  Upload,
} from "lucide-react";
import { useStore } from "@/lib/store";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import {
  useCreativePipeline,
  useMoveTask,
  useGenerateAssetForTask,
  useArchiveTask,
  useUploadTaskToMeta,
  useCreateTask,
} from "@/hooks/useCreativePipeline";
import { usePresignUpload } from "@/hooks/useFirmAssets";
import { useAutopilotSessions } from "@/hooks/useAutopilot";
import { useToast } from "@/hooks/use-toast";
import { AutopilotPageShell } from "@/components/autopilot/AutopilotPageHeader";
import { UploadToMetaModal } from "@/components/autopilot/UploadToMetaModal";
import {
  KanbanBoard,
  type KanbanColumn,
  type KanbanTask,
} from "@/components/ui/trello-kanban-board";
import type { CreativePipelineTask } from "@shared/schema";

const COLUMN_LABELS: Record<string, string> = {
  pomysly: "Pomysły",
  "do-nagrania": "Do nagrania",
  "do-montazu": "Do montażu",
  "do-zatwierdzenia": "Do zatwierdzenia",
  "gotowe-do-testu": "Gotowe do testu",
  "w-trakcie-testu": "W trakcie testu",
  zwycieskie: "Zwycięskie",
  niedzialajace: "Niedziałające",
  "przestalo-dzialac": "Przestało działać",
};

const COLUMN_ORDER = [
  "pomysly",
  "do-nagrania",
  "do-montazu",
  "do-zatwierdzenia",
  "gotowe-do-testu",
  "w-trakcie-testu",
  "zwycieskie",
  "niedzialajace",
  "przestalo-dzialac",
];

const COLUMN_COLORS: Record<string, string> = {
  pomysly: "bg-slate-500",
  "do-nagrania": "bg-blue-500",
  "do-montazu": "bg-indigo-500",
  "do-zatwierdzenia": "bg-purple-500",
  "gotowe-do-testu": "bg-cyan-500",
  "w-trakcie-testu": "bg-amber-500",
  zwycieskie: "bg-emerald-500",
  niedzialajace: "bg-red-500",
  "przestalo-dzialac": "bg-orange-500",
};

const LABEL_COLORS: Record<string, string> = {
  manual: "bg-slate-500",
  meta: "bg-blue-500",
  grafika: "bg-pink-500",
  autopilot: "bg-violet-500",
  winner: "bg-emerald-500",
  fatigued: "bg-orange-500",
  inspiracja: "bg-amber-500",
  "screen→wariacja": "bg-purple-500",
};

const SOURCE_TO_LABEL: Record<string, string> = {
  manual: "manual",
  meta_import: "meta",
  graphics_import: "grafika",
  autopilot_brief: "autopilot",
  autopilot_winner: "winner",
  autopilot_fatigued: "fatigued",
  inspiration_link: "inspiracja",
  inspiration_screenshot: "screen→wariacja",
};

function mapTaskToKanban(task: CreativePipelineTask): KanbanTask {
  const meta = task.metadata as Record<string, unknown> | null;
  const screenshotUrl =
    meta && typeof meta.screenshotUrl === "string" ? meta.screenshotUrl : undefined;
  const labels = [SOURCE_TO_LABEL[task.sourceType] ?? "manual"];
  if (task.mediaType === "video") labels.push("video");
  return {
    id: task.id,
    title: task.title,
    description: task.description ?? undefined,
    labels,
    imageUrl: task.thumbnailUrl ?? screenshotUrl ?? task.mediaUrl ?? undefined,
    thumbnailUrl: task.thumbnailUrl ?? screenshotUrl ?? undefined,
    fileUrl: task.mediaUrl ?? undefined,
    createdAt: typeof task.createdAt === "string" ? task.createdAt : task.createdAt?.toISOString(),
  };
}

export default function AutopilotPipelinePage() {
  const selectedClientId = useStore((s) => s.selectedClientId);
  const selectedClientName = useStore((s) => s.selectedClientName);
  const scopeKey = selectedClientId ?? null;
  const { data, isLoading } = useCreativePipeline(scopeKey);
  const moveMut = useMoveTask(scopeKey);
  const generateMut = useGenerateAssetForTask(scopeKey);
  const archiveMut = useArchiveTask(scopeKey);
  const uploadMut = useUploadTaskToMeta(scopeKey);
  const createTaskMut = useCreateTask(scopeKey);
  const presignMut = usePresignUpload();
  const { data: sessions } = useAutopilotSessions();
  const { toast } = useToast();
  const [uploadModalTask, setUploadModalTask] = useState<CreativePipelineTask | null>(null);
  const manualFileRef = useRef<HTMLInputElement>(null);
  const [manualUploading, setManualUploading] = useState(false);

  const handleManualUpload = async (file: File) => {
    setManualUploading(true);
    try {
      const presign = await presignMut.mutateAsync({
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
      });
      const putRes = await fetch(presign.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putRes.ok) throw new Error(`R2 upload failed: ${putRes.status}`);
      const mediaType = file.type.startsWith("video/") ? "video" : "image";
      await createTaskMut.mutateAsync({
        agencyClientId: scopeKey,
        columnId: "gotowe-do-testu",
        sourceType: "manual",
        title: file.name.replace(/\.[^.]+$/, ""),
        description: "Wgrana ręcznie przez user-a",
        mediaType,
        mediaUrl: presign.getUrl,
        thumbnailUrl: mediaType === "image" ? presign.getUrl : null,
        metadata: { uploadedAt: new Date().toISOString(), source: "manual_upload" },
      });
      toast({
        title: "Kreacja dodana",
        description: `${file.name} → "Gotowe do testu". Możesz teraz wgrać na Meta.`,
      });
    } catch (err) {
      toast({
        title: "Nie udało się wgrać",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    } finally {
      setManualUploading(false);
      if (manualFileRef.current) manualFileRef.current.value = "";
    }
  };

  const tasksByColumn = new Map<string, CreativePipelineTask[]>();
  for (const colId of COLUMN_ORDER) tasksByColumn.set(colId, []);
  for (const col of data?.columns ?? []) {
    if (tasksByColumn.has(col.id)) tasksByColumn.set(col.id, col.tasks);
  }
  const totalTasks = Array.from(tasksByColumn.values()).reduce((sum, t) => sum + t.length, 0);

  const columns: KanbanColumn[] = COLUMN_ORDER.map((colId) => ({
    id: colId,
    title: COLUMN_LABELS[colId],
    tasks: (tasksByColumn.get(colId) ?? []).map(mapTaskToKanban),
  }));

  const handleTaskMove = (taskId: string, _fromColumnId: string, toColumnId: string) => {
    moveMut.mutate({ id: taskId, columnId: toColumnId, position: 0 });
  };

  const handleGenerateAsset = async (taskId: string) => {
    try {
      const result = await generateMut.mutateAsync(taskId);
      if (!result.ok) {
        toast({
          title: "Generowanie nie udało się",
          description:
            result.error === "ugc_requires_manual_heygen_setup"
              ? "UGC wymaga setupu HeyGen avatar+voice — zrób to przez ad-creator HeyGen flow."
              : (result.error ?? "Nieznany błąd"),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Wizualizacja gotowa",
        description: 'Karta przeniesiona do "Gotowe do testu".',
      });
    } catch (err) {
      toast({
        title: "Błąd",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    }
  };

  // Briefy AI oczekujące na akceptację — autopilot_brief w kolumnie "pomysly" bez mediaUrl.
  const pendingBriefs = (tasksByColumn.get("pomysly") ?? []).filter(
    (t) => t.sourceType === "autopilot_brief" && !t.mediaUrl
  );
  // Wariacje wygenerowane oczekujące na zatwierdzenie.
  const pendingApproval = tasksByColumn.get("do-zatwierdzenia") ?? [];
  // Zatwierdzone, gotowe do uploadu na Meta — sourceType=autopilot_brief w 'gotowe-do-testu' bez metaAdId.
  const readyToUpload = (tasksByColumn.get("gotowe-do-testu") ?? []).filter((t) => {
    const meta = t.metadata as Record<string, unknown> | null;
    return t.sourceType === "autopilot_brief" && !meta?.metaAdId;
  });

  const handleApprove = (taskId: string, sessionId: string | null) => {
    moveMut.mutate(
      { id: taskId, columnId: "gotowe-do-testu", position: 0 },
      {
        onSuccess: () => {
          toast({ title: "Zatwierdzono", description: 'Karta przeniesiona do "Gotowe do testu".' });
          // Auto-upload na Meta jeśli session.config.autoUploadOnApprove === true
          if (!sessionId) return;
          const session = sessions?.find((s) => s.id === sessionId);
          const cfg = (session?.config ?? {}) as Record<string, unknown>;
          if (cfg.autoUploadOnApprove !== true) return;
          const mode =
            (cfg.defaultUploadMode as
              | "duplicate_winner"
              | "duplicate_specific"
              | "add_to_existing"
              | "create_new") ?? "duplicate_winner";
          const status: "PAUSED" | "ACTIVE" =
            cfg.defaultUploadStatus === "ACTIVE" ? "ACTIVE" : "PAUSED";
          // Tylko duplicate_winner / add_to_existing są w pełni auto-able. Inne wymagają user input (target adset / audience).
          if (mode !== "duplicate_winner") {
            toast({
              title: "Auto-upload pominięty",
              description: `Domyślny tryb "${mode}" wymaga wyboru w modalu. Kliknij "Wgraj na Meta" ręcznie.`,
            });
            return;
          }
          uploadMut.mutate(
            { taskId, options: { mode: "duplicate_winner", status } },
            {
              onSuccess: (r) => {
                if (r.ok) {
                  toast({
                    title: "Auto-upload",
                    description: `Reklama wgrana jako ${status} w adset ${r.metaAdsetId?.slice(-8) ?? ""}.`,
                  });
                } else {
                  toast({
                    title: "Auto-upload nie udał się",
                    description:
                      r.error === "no_winner_adset_found"
                        ? 'Brak winnera — kliknij "Wgraj na Meta" i wybierz tryb ręcznie.'
                        : (r.error ?? "Nieznany błąd"),
                    variant: "destructive",
                  });
                }
              },
              onError: (err) =>
                toast({
                  title: "Auto-upload — błąd",
                  description: FriendlyErrorMapper.map(err).description,
                  variant: "destructive",
                }),
            }
          );
        },
        onError: (err) =>
          toast({
            title: "Nie udało się zatwierdzić",
            description: FriendlyErrorMapper.map(err).description,
            variant: "destructive",
          }),
      }
    );
  };

  const handleReject = (taskId: string, label: string) => {
    if (!confirm(`Odrzucić wariant "${label}"? Karta zostanie zarchiwizowana.`)) return;
    archiveMut.mutate(taskId, {
      onSuccess: () =>
        toast({
          title: "Wariant odrzucony",
          description:
            "Karta zarchiwizowana — autopilot wygeneruje nowe próby przy kolejnym cyklu.",
        }),
      onError: (err) =>
        toast({
          title: "Nie udało się odrzucić",
          description: FriendlyErrorMapper.map(err).description,
          variant: "destructive",
        }),
    });
  };

  return (
    <AutopilotPageShell
      title="Pipeline autopilota"
      subtitle={
        <>
          Klient: <Badge variant="secondary">{selectedClientName ?? "Globalna"}</Badge>
          {" · "}
          {totalTasks} kart
        </>
      }
      maxWidthClass="max-w-[1600px]"
      actions={
        <>
          <Link href="/admin/autopilot/inspiration">
            <Button size="sm" variant="outline" className="gap-2">
              <Lightbulb className="h-4 w-4" /> Dodaj inspirację
            </Button>
          </Link>
          <Link href="/firm-assets">
            <Button size="sm" variant="outline" className="gap-2">
              <Sparkles className="h-4 w-4" /> Firm assets
            </Button>
          </Link>
        </>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <>
          <Card className="border-blue-500/40 mb-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Upload className="h-4 w-4 text-blue-400" /> Wgraj swoją kreację (manual upload)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">
                Masz gotową grafikę / video? Wgraj plik — wpadnie do "Gotowe do testu" i będzie
                można je puścić na Meta. Działa w każdym creativeMode (AI / manual / mix).
              </p>
              <input
                ref={manualFileRef}
                type="file"
                accept="image/*,video/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleManualUpload(f);
                }}
                className="hidden"
              />
              <Button
                size="sm"
                onClick={() => manualFileRef.current?.click()}
                disabled={manualUploading}
                className="gap-2"
              >
                {manualUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {manualUploading ? "Wgrywanie…" : "Wybierz plik (image/video)"}
              </Button>
            </CardContent>
          </Card>

          {pendingApproval.length > 0 && (
            <Card className="border-purple-500/40 mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Check className="h-4 w-4 text-purple-400" /> Wariacje oczekujące na zatwierdzenie
                  ({pendingApproval.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">
                  AI wygenerowało wizualizacje. Wybierz najlepsze wariacje —{" "}
                  <strong>Zatwierdź</strong> przesuwa do "Gotowe do testu", <strong>Odrzuć</strong>{" "}
                  archiwizuje słabe.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pendingApproval.map((task) => (
                    <div key={task.id} className="border rounded p-2 space-y-2">
                      {task.thumbnailUrl ? (
                        task.mediaType === "video" ? (
                          <video
                            src={task.mediaUrl ?? task.thumbnailUrl}
                            controls
                            className="w-full rounded max-h-48 object-cover"
                          />
                        ) : (
                          <img
                            src={task.thumbnailUrl}
                            alt={task.title}
                            className="w-full rounded max-h-48 object-cover"
                          />
                        )
                      ) : (
                        <div className="w-full h-32 bg-white/5 rounded flex items-center justify-center text-xs text-muted-foreground">
                          brak podglądu
                        </div>
                      )}
                      <p className="text-xs font-medium line-clamp-2" title={task.title}>
                        {task.title}
                      </p>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          onClick={() => handleApprove(task.id, task.autopilotSessionId ?? null)}
                          disabled={moveMut.isPending}
                          className="flex-1 gap-1 bg-emerald-600 hover:bg-emerald-500"
                        >
                          <Check className="h-3.5 w-3.5" /> Zatwierdź
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReject(task.id, task.title)}
                          disabled={archiveMut.isPending}
                          className="gap-1 text-red-400 border-red-500/30 hover:bg-red-500/10"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {pendingBriefs.length > 0 && (
            <Card className="border-primary/40 mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Wand2 className="h-4 w-4" /> Briefy AI oczekujące na akceptację (
                  {pendingBriefs.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">
                  Autopilot wygenerował briefy (hooki, headlines, CTA). Klikasz "Wygeneruj
                  wizualizację" → fal/Kling produkuje obraz/video → karta przenosi się do "Gotowe do
                  testu".
                </p>
                <div className="grid gap-2">
                  {pendingBriefs.map((task) => {
                    const meta = task.metadata as Record<string, unknown> | null;
                    const ctype = (meta?.creativeType as string) ?? "graphics";
                    const status = (meta?.generationStatus as string) ?? "pending_user_approval";
                    const error = (meta?.generationError as string) ?? null;
                    const isUgc = ctype === "ugc";
                    return (
                      <div
                        key={task.id}
                        className="border rounded p-3 flex items-start justify-between gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{task.title}</p>
                          {task.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                              {task.description}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-1 mt-1">
                            <Badge variant="outline" className="text-[10px]">
                              {ctype}
                            </Badge>
                            {status === "failed" && (
                              <Badge variant="destructive" className="text-[10px] gap-1">
                                <AlertTriangle className="h-3 w-3" /> błąd
                              </Badge>
                            )}
                          </div>
                          {error && (
                            <p className="text-[10px] text-red-400 mt-1 truncate">{error}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleGenerateAsset(task.id)}
                          disabled={generateMut.isPending || isUgc}
                          title={
                            isUgc
                              ? "UGC wymaga ręcznego setup HeyGen"
                              : "Odpal AI generację (fal/Kling)"
                          }
                          className="gap-2 shrink-0"
                        >
                          <Wand2
                            className={`h-3.5 w-3.5 ${generateMut.isPending ? "animate-pulse" : ""}`}
                          />
                          {isUgc ? "UGC ręcznie" : "Wygeneruj"}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          {readyToUpload.length > 0 && (
            <Card className="border-cyan-500/40 mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Rocket className="h-4 w-4 text-cyan-400" /> Gotowe do wgrania na Meta (
                  {readyToUpload.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">
                  Zatwierdzone wariacje czekają na upload do konta Meta. Klikasz "Wgraj na Meta" —
                  wybierasz tryb (duplikat winnera / inny adset / dodać do istniejącego), status
                  (PAUSED/ACTIVE) i lecimy.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {readyToUpload.map((task) => {
                    const sessionId = task.autopilotSessionId;
                    return (
                      <div
                        key={task.id}
                        className="border rounded p-2 flex items-start justify-between gap-3"
                      >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          {task.thumbnailUrl && (
                            <img
                              src={task.thumbnailUrl}
                              alt=""
                              className="w-12 h-12 rounded object-cover shrink-0"
                            />
                          )}
                          <p className="text-xs font-medium line-clamp-2" title={task.title}>
                            {task.title}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => setUploadModalTask(task)}
                          disabled={!sessionId}
                          title={
                            sessionId
                              ? "Wgraj kreację na konto Meta"
                              : "Task nie jest powiązany z sesją autopilota"
                          }
                          className="gap-2 shrink-0"
                        >
                          <Rocket className="h-3.5 w-3.5" />
                          Wgraj na Meta
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          <KanbanBoard
            columns={columns}
            onTaskMove={handleTaskMove}
            columnColors={COLUMN_COLORS}
            labelColors={LABEL_COLORS}
          />
        </>
      )}

      {uploadModalTask && (
        <UploadToMetaModal
          open={!!uploadModalTask}
          onClose={() => setUploadModalTask(null)}
          taskId={uploadModalTask.id}
          taskTitle={uploadModalTask.title}
          sessionId={uploadModalTask.autopilotSessionId ?? null}
          agencyClientId={scopeKey}
        />
      )}
    </AutopilotPageShell>
  );
}
